import * as React from 'react';

import {
  createStyles,
  Grid,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@nelnet/unifi-components-react';

import UnifiTheme from '../../../themes/unifi/UnifiTheme';
import Link from '../../modules/Link';

const hero = require('../../../assets/images/hero.svg');
const studioIcon = require('../../../assets/images/studio-icon.svg');

const styles = ({ spacing, breakpoints, palette }: Theme) =>
  createStyles({
    wrapper: {
      margin: `0 auto ${spacing(3)}px`,
      [breakpoints.up('md')]: {
        '& > div + div': {
          paddingLeft: spacing(3),
        },
      },
    },
    heroWrap: {
      width: '100%',
      backgroundColor: palette.primary.main,
      padding: `${spacing(10)}px ${spacing(3)}px ${spacing(4)}px`,
      [breakpoints.up('md')]: {
        paddingLeft: `${spacing(6)}px`,
        marginLeft: '240px',
      },
      [breakpoints.down('xs')]: {
        paddingTop: spacing(12),
      },
    },
    content: {
      '& h1, & h2, & p': {
        color: '#fff',
      },
      '& h1': {
        fontWeight: 600,
        fontSize: 'calc(2.5rem + 3 * ((100vw - 320px) / 300))',
      },
      '& h1:focus': {
        outline: 'none',
      },
      '& a': {
        textDecoration: 'underline',
        color: 'inherit !important',
        '&:hover': {
          textDecoration: 'none',
        },
      },
      '& img': {
        display: 'block',
        transition: '340ms all ease-in',
        transform: 'translateX(-100%)',
        opacity: '0',
        '&.move': {
          transform: 'translateX(0)',
          opacity: '1',
        },
      },
      [breakpoints.up('md')]: {
        paddingRight: spacing(2),
      },
    },
    graphic: {
      flexGrow: 1,
      '& img': {
        height: '35vh',
        transform: 'translateY(5%)',
        flexGrow: 1,
        marginLeft: 'auto',
      },
      [breakpoints.up('md')]: {
        height: '25vh',
      },
      [breakpoints.down('sm')]: {
        marginTop: spacing(3),
        '& img': {
          maxHeight: '30vh',
        },
      },
    },
  });

const HomeHero: React.FunctionComponent<WithStyles<typeof styles>> = ({ classes }) => {
  const imgMove = React.createRef<HTMLImageElement>();

  React.useEffect(() => {
    const img = imgMove.current!;
    setTimeout(() => {
      img.classList.add('move');
    }, 600);
  }, []);

  return (
    <div className={classes.heroWrap}>
      <Grid container constrained component="section" className={classes.wrapper}>
        <Grid
          item
          container
          direction="column"
          justify="flex-end"
          md={7}
          lg={6}
          className={classes.content}
        >
          <UnifiTheme>
            <Typography variant="h1" tabIndex={-1}>
              Unifi Design&nbsp;System
            </Typography>
            <Typography variant="body1" margin={{ top: 2 }}>
              A collection of components and guidelines to develop and design innovative, consistent
              and delightful products.
            </Typography>
            <Grid container spacing={2} alignItems="center" margin={{ top: 2 }}>
              <Grid item xs="auto">
                <img ref={imgMove} src={studioIcon} role="presentation" alt="" />
              </Grid>
              <Grid item xs>
                <Typography variant="body1">
                  <strong>
                    More than a design system, the{' '}
                    <Link href="#collective" color="inherit" underline="always">
                      Unifi Collective
                    </Link>{' '}
                    has grown to also include the Unifi Design Studio.
                  </strong>
                </Typography>
              </Grid>
            </Grid>
          </UnifiTheme>
        </Grid>
        <Grid item container alignItems="flex-end" md={5} lg={6} className={classes.graphic}>
          <img src={hero} role="presentation" alt="" />
        </Grid>
      </Grid>
    </div>
  );
};

export default withStyles(styles)(HomeHero);
